// ─── Error Service ──────────────────────────────────────────────────────
// Centralized error reporting: console + Sentry + optional toast.
// Every catch block in the app should call reportError() instead of
// console.warn/console.error directly. This ensures all failures are
// visible in Sentry dashboards and optionally surfaced to the user.
//
// Severity tiers:
//   critical — user-initiated action failed (route search, trip save, login).
//              Logged to Sentry as "error", caller can toast.
//   warning  — degraded functionality (ghost leg, station resolution).
//              Logged to Sentry as "warning", silent to user.
//   info     — cosmetic/enrichment failure (Wikipedia image, POI badge).
//              Added as Sentry breadcrumb only (no event). Silent.

import * as Sentry from "@sentry/react";

export type ErrorLevel = "critical" | "warning" | "info";

export interface ErrorContext {
  /** Severity tier — determines Sentry level + whether to alert */
  level: ErrorLevel;
  /** Freeform context tags for Sentry (e.g. { from: "Berlin", to: "Paris" }) */
  context?: Record<string, string>;
  /** If true, suppress console output (default: false) */
  silent?: boolean;
}

/**
 * Report an error to console + Sentry.
 * Returns the error for convenient chaining (`throw reportError(err, ...)`).
 */
export function reportError(error: unknown, opts: ErrorContext): Error {
  const err = error instanceof Error ? error : new Error(String(error));
  const { level, context, silent } = opts;

  // ─── Console (always, for local dev) ─────────────────────────
  if (!silent) {
    if (level === "critical") {
      console.error(`[${level}]`, err.message, context ?? "");
    } else if (level === "warning") {
      console.warn(`[${level}]`, err.message, context ?? "");
    } else if (import.meta.env.DEV) {
      // info — only in dev to reduce noise
      console.debug(`[${level}]`, err.message, context ?? "");
    }
  }

  // ─── Sentry ──────────────────────────────────────────────────
  const sentryContext = context
    ? { tags: context, level: sentryLevel(level) }
    : { level: sentryLevel(level) };

  if (level === "info") {
    // Breadcrumb only — doesn't create a Sentry event
    Sentry.addBreadcrumb({
      category: "error.info",
      message: err.message,
      level: "info",
      data: context,
    });
  } else {
    // Critical + Warning → full Sentry event
    Sentry.captureException(err, sentryContext);
  }

  return err;
}

function sentryLevel(level: ErrorLevel): "error" | "warning" | "info" {
  switch (level) {
    case "critical": return "error";
    case "warning":  return "warning";
    case "info":     return "info";
  }
}
